import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  Layers,
  Building2,
  Newspaper,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Преглед', icon: LayoutDashboard, path: '/cms' },
  { label: 'Настани', icon: Calendar, path: '/cms/events' },
  { label: 'Регистрации', icon: FileText, path: '/cms/registrations' },
  { label: 'Серии', icon: Layers, path: '/cms/series' },
  { label: 'Организатори', icon: Building2, path: '/cms/organizers', adminOnly: true },
  { label: 'Постови', icon: Newspaper, path: '/cms/posts' },
  { label: 'AI Dashboard', icon: Sparkles, path: '/cms/ai', adminOnly: true },
];

const SIDEBAR_COLLAPSED_KEY = 'cms-sidebar-collapsed';

interface CmsSidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const CmsSidebar: React.FC<CmsSidebarProps> = ({ mobileOpen, onMobileClose, collapsed: controlledCollapsed, onToggleCollapse }) => {
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  });

  const collapsed = controlledCollapsed ?? internalCollapsed;
  const toggleCollapse = onToggleCollapse ?? (() => setInternalCollapsed(c => !c));
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (controlledCollapsed === undefined) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    }
  }, [collapsed, controlledCollapsed]);

  const isActive = (path: string) => {
    if (path === '/cms') return location.pathname === '/cms';
    return location.pathname.startsWith(path);
  };

  const filteredItems = navItems.filter(
    (item) => !item.adminOnly || isAdmin()
  );

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo area */}
      <div className="flex items-center h-14 px-4 border-b border-border shrink-0">
        <button
          onClick={() => handleNav('/cms')}
          className="flex items-center space-x-2 overflow-hidden"
        >
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z" />
            </svg>
          </div>
          {!collapsed && (
            <span className="text-lg font-bold text-foreground whitespace-nowrap">
              drimcms
            </span>
          )}
        </button>
      </div>

      {/* Nav items */}
      <ScrollArea className="flex-1 py-2">
        <TooltipProvider delayDuration={0}>
          <nav className="space-y-1 px-2">
            {filteredItems.map((item) => {
              const active = isActive(item.path);
              const button = (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={cn(
                    'flex items-center w-full rounded-md text-sm font-medium transition-colors',
                    collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5 gap-3',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <item.icon className={cn('shrink-0', collapsed ? 'w-5 h-5' : 'w-5 h-5')} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </button>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              }

              return button;
            })}
          </nav>
        </TooltipProvider>
      </ScrollArea>

      {/* Collapse toggle (desktop only) */}
      <div className="hidden md:flex items-center justify-center border-t border-border p-2 shrink-0">
        <button
          onClick={toggleCollapse}
          className="flex items-center justify-center w-full rounded-md py-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4 mr-2" />
              <span className="text-xs">Собери</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-background border-r border-border w-64 transform transition-transform duration-200 md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden md:flex md:flex-col md:shrink-0 bg-background border-r border-border transition-all duration-200',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default CmsSidebar;
